import { useEffect, useState } from 'react';
import { fetchStatus, fetchHistory, fetchIncidents } from './lib/api';
import type { StatusSnapshot, UptimeHistory, Incident } from './lib/types';
import { StatusBanner } from './components/StatusBanner';
import { IncidentBanner } from './components/IncidentBanner';
import { ScheduledNotice } from './components/ScheduledNotice';
import { ComponentRow } from './components/ComponentRow';
import { IncidentTimeline } from './components/IncidentTimeline';
import { Footer } from './components/Footer';

const REFRESH_MS = 60_000;
// 90 days of incidents to cover the full day-cell range. Cheap to pull -- a
// well-managed site has tens of incidents per quarter, not thousands.
const INCIDENT_HISTORY_LIMIT = 200;

function App() {
  const [snapshot, setSnapshot] = useState<StatusSnapshot | null>(null);
  const [history, setHistory] = useState<Record<string, UptimeHistory['days']>>({});
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function loadSnapshot() {
    setRefreshing(true);
    try {
      const s = await fetchStatus();
      setSnapshot(s);
      setErr(null);
      setLastRefreshed(new Date());
    } catch (e) {
      setErr(String(e));
    } finally {
      setRefreshing(false);
    }
  }

  async function loadHistory() {
    try {
      const h = await fetchHistory();
      const byId: Record<string, UptimeHistory['days']> = {};
      for (const c of h.components) byId[c.componentId] = c.days;
      setHistory(byId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('history load failed', e);
    }
  }

  async function loadIncidents() {
    try {
      const r = await fetchIncidents(INCIDENT_HISTORY_LIMIT);
      setIncidents(r.incidents);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('incidents load failed', e);
    }
  }

  useEffect(() => {
    loadSnapshot();
    loadHistory();
    loadIncidents();
    const id = setInterval(loadSnapshot, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const groups: Record<string, StatusSnapshot['components']> = {};
  if (snapshot) {
    for (const c of snapshot.components) {
      groups[c.group] ??= [];
      groups[c.group].push(c);
    }
  }

  return (
    <div className="min-h-full bg-[var(--color-bg)] text-[var(--color-fg)]">
      <header className="fixed top-0 right-0 left-0 z-50 flex h-16 items-center bg-[var(--color-header-bg)] shadow-md">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-4 px-4 sm:px-6">
          <a
            href="/"
            className="flex items-center gap-3 text-white no-underline"
            aria-label="UNREALITY status home"
          >
            <img
              src="/logo-large.png"
              alt="Unreality"
              className="h-7 w-auto"
              width="3238"
              height="610"
            />
            <span className="font-mono text-xs tracking-wider text-white/70 uppercase">
              status
            </span>
          </a>
          <a
            href="https://unreality.is"
            className="font-mono text-xs tracking-wider text-white/70 uppercase transition-colors hover:text-white"
          >
            unreality.is →
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-6 px-4 pt-24 pb-12 sm:px-6">
        {err && !snapshot && (
          <div className="border border-status-major bg-status-major/15 px-4 py-3 text-sm text-white">
            Could not load status. The page will retry automatically.
          </div>
        )}

        {snapshot && <StatusBanner status={snapshot.overall} />}

        {snapshot?.activeIncidents.map((inc) => (
          <IncidentBanner key={inc.id} incident={inc} />
        ))}

        {snapshot?.scheduledMaintenance.map((inc) => (
          <ScheduledNotice key={inc.id} incident={inc} />
        ))}

        {snapshot && (
          <section className="overflow-hidden border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
            {Object.entries(groups).map(([group, comps], idx, arr) => (
              <div key={group}>
                <div className="font-mono border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] px-5 py-3 text-xs font-medium uppercase tracking-wider text-[var(--color-fg-muted)]">
                  {group}
                </div>
                <div className="px-5">
                  {comps.map((c) => (
                    <ComponentRow
                      key={c.id}
                      component={c}
                      history={history[c.id]}
                      incidents={incidents}
                    />
                  ))}
                </div>
                {idx < arr.length - 1 && <div className="h-2 bg-[var(--color-bg-subtle)]" />}
              </div>
            ))}
          </section>
        )}

        <section>
          <h2 className="font-display mb-4 mt-10 text-base font-medium text-[var(--color-fg)]">
            Incident History
          </h2>
          <IncidentTimeline incidents={incidents} />
        </section>

        <Footer lastRefreshed={lastRefreshed} refreshing={refreshing} />
      </main>
    </div>
  );
}

export default App;
